/**
 * NFDH (Next Fit Decreasing Height) bin packing algorithm implementation
 * This algorithm is used to pack rectangles into rows with a fixed width.
 * It sorts rectangles by height and places them in rows until the row width limit is reached.
 */

import { IRoom } from 'Plugins/FloorPlan/Models/IRoom';
import { LaizeCalcProps } from '../LaizeCalc';
import { StairsResult } from '../Laize';
import { Logger } from 'Errors/Logger';
import { Band, LaizeStairs, SingleStairStep } from './LaizeStairs';
import { Toast } from 'Web/Services/ToastService';

interface Rectangle {
    id: string;
    width: number;
    length: number;
}

interface PlacedRectangle extends Rectangle {
    x: number;
    y: number;
    row: number;
}

interface Row {
    rectangles: PlacedRectangle[];
    currentWidth: number;
    height: number;
    y: number;
}

export class NFDHOptimizer {
    private rollWidth: number;
    private rows: Row[] = [];
    private totalLength: number = 0;

    constructor(rollWidth: number = 400) {
        this.rollWidth = rollWidth;
    }

    /**
     * Check if each rectangle width is less than or equal to the roll width.
     * @param rectangles Array of rectangles to check
     * @returns True if all widths fit, false otherwise
     */
    public canAllWidthsFit(rectangles: Rectangle[]): boolean {
        return rectangles.every((r) => r.width <= this.rollWidth);
    }

    /**
     * Optimize rectangle placement using NFDH algorithm
     * @param rectangles Array of rectangles to place
     * @returns Object containing placed rectangles and total roll length used
     */
    optimize(rectangles: Rectangle[]): {
        placedRectangles: PlacedRectangle[];
        rows: Row[];
        totalLength: number;
        efficiency: number;
    } {
        // Reset state
        this.rows = [];
        this.totalLength = 0;

        // Sort rectangles by height (length) in descending order
        const sortedRectangles = [...rectangles].sort((a, b) => b.length - a.length);

        // Place each rectangle
        for (const rectangle of sortedRectangles) {
            this.placeRectangle(rectangle);
        }

        // Calculate efficiency
        const totalRectangleArea = rectangles.reduce((sum, rect) => sum + rect.width * rect.length, 0);
        const totalUsedArea = this.totalLength * this.rollWidth;
        const efficiency = (totalRectangleArea / totalUsedArea) * 100;

        return {
            placedRectangles: this.getAllPlacedRectangles(),
            rows: this.rows,
            totalLength: this.totalLength,
            efficiency,
        };
    }

    private placeRectangle(rectangle: Rectangle): void {
        // Try to place in existing rows first
        for (let i = 0; i < this.rows.length; i++) {
            if (this.canFitInRow(rectangle, this.rows[i])) {
                this.addToRow(rectangle, this.rows[i], i);
                return;
            }
        }

        // Create new row if rectangle doesn't fit in any existing row
        this.createNewRow(rectangle);
    }

    private canFitInRow(rectangle: Rectangle, row: Row): boolean {
        return row.currentWidth + rectangle.width <= this.rollWidth && rectangle.length <= row.height;
    }

    private addToRow(rectangle: Rectangle, row: Row, rowIndex: number): void {
        const placedRect: PlacedRectangle = {
            ...rectangle,
            x: row.currentWidth,
            y: row.y,
            row: rowIndex,
        };

        row.rectangles.push(placedRect);
        row.currentWidth += rectangle.width;
    }

    private createNewRow(rectangle: Rectangle): void {
        const newRow: Row = {
            rectangles: [],
            currentWidth: 0,
            height: rectangle.length,
            y: this.totalLength,
        };

        this.addToRow(rectangle, newRow, this.rows.length);
        this.rows.push(newRow);
        this.totalLength += rectangle.length;
    }

    private getAllPlacedRectangles(): PlacedRectangle[] {
        return this.rows.flatMap((row) => row.rectangles);
    }

    /**
     * Generate a visual representation of the cutting layout
     */
    generateLayout(): string {
        let layout = `Roll Layout (Width: ${this.rollWidth}cm, Total Length: ${this.totalLength}cm)\n`;
        layout += '='.repeat(60) + '\n';

        this.rows.forEach((row, index) => {
            layout += `Row ${index + 1} (Height: ${row.height}cm, Y: ${row.y}cm)\n`;
            layout += `├─ Used Width: ${row.currentWidth}cm / ${this.rollWidth}cm\n`;
            layout += `├─ Rectangles: ${row.rectangles.length}\n`;

            row.rectangles.forEach((rect, rectIndex) => {
                const prefix = rectIndex === row.rectangles.length - 1 ? '└─' : '├─';
                layout += `${prefix} ${rect.id}: ${rect.width}×${rect.length}cm at (${rect.x}, ${rect.y})\n`;
            });
            layout += '\n';
        });

        return layout;
    }
}

export const binPackingNFDHStairsUnpacked = (
    stairsUnpacked: Array<SingleStairStep>,
    laizeMargin: number,
    rooms: Array<IRoom>,
    band: Band,
    laizeCalcProps: LaizeCalcProps,
    logName?: string
): { stairsResults: Array<StairsResult>; totalLength: number } => {
    const sousFamilleProduit = laizeCalcProps.LAIZE_PROPS.produit?.sous_famille_produit_complete_code;
    const shouldComputeVertically = sousFamilleProduit === 'F004_S035' || sousFamilleProduit === 'F006_S041'; // Jonc de mer ou Rouleau de vinyle (https://docs.google.com/presentation/d/1rw1-P0sJcUVonQf0FHxZqRdrn2n5K84zAFKCTuQHGd4)
    const optimizer = new NFDHOptimizer(laizeCalcProps.BAND_WIDTH);
    const singleStairSteps = stairsUnpacked.map((stair) => {
        const width = stair.marches + laizeMargin;
        const length = stair.giron + (stair.contremarches ?? stair.giron) + laizeMargin;
        return {
            id: stair.id,
            width: shouldComputeVertically ? length : width,
            length: shouldComputeVertically ? width : length,
        };
    });
    if (!optimizer.canAllWidthsFit(singleStairSteps)) {
        Logger.logAnalytics({
            message: logName ?? 'POL: LaizeStairs.calculLaizeStairs',
            value: {
                error: 'One or more stairs exceed the band width',
                context: {
                    laizeCalcProps: laizeCalcProps,
                    stairs: rooms.map((r) => r.stairsSteps),
                },
                rawContext: JSON.stringify({
                    laizeCalcProps: laizeCalcProps,
                    stairs: rooms.map((r) => r.stairsSteps),
                }),
            },
        });
        Toast.error('StairsLaize_WidthOverflow_Error');

        return { stairsResults: [], totalLength: 0 };
    }
    const solution = optimizer.optimize(singleStairSteps);

    const stairsResults: Array<StairsResult> = [];

    let currentCursorHeight = 0;
    let currentCursorWidth = 0;
    let wholeIndex = 0;
    for (const [rowIndex, row] of solution.rows.entries()) {
        currentCursorWidth = 0;
        for (const [rectIndex, rect] of row.rectangles.entries()) {
            const stairWithMargin = LaizeStairs.createRectangleFromCursorPosition(
                currentCursorHeight,
                currentCursorWidth,
                rect.width,
                rect.length
            );

            const originalStair = stairsUnpacked.find((s) => s.id === rect.id)!;
            const stairWithMarginCoords = stairWithMargin.getCoordinates();
            const stairCoords = stairWithMargin.buffer(-laizeMargin / 2).getCoordinates();
            stairsResults.push({
                points: stairCoords,
                contremarcheLine: shouldComputeVertically
                    ? {
                          p1: {
                              x: stairCoords[0].x + (originalStair.contremarches ?? 0),
                              y: stairCoords[0].y,
                          },
                          p2: {
                              x: stairCoords[3].x + (originalStair.contremarches ?? 0),
                              y: stairCoords[3].y,
                          },
                      }
                    : {
                          p1: {
                              x: stairCoords[0].x,
                              y: stairCoords[0].y - (originalStair.contremarches ?? 0),
                          },
                          p2: {
                              x: stairCoords[1].x,
                              y: stairCoords[1].y - (originalStair.contremarches ?? 0),
                          },
                      },
                pointsWithMargin: stairWithMarginCoords,
                type: originalStair.type,
                bandIndex: rowIndex,
                bandId: `band-${rowIndex}-staircase-${originalStair.staircaseId}`,
                indexOnBand: rectIndex,
                wholeIndex: wholeIndex,
                stairId: rect.id,
                staircaseId: originalStair.staircaseId,
                staircaseIndex: originalStair.staircaseIndex,
                staircaseName: originalStair.staircaseName,
                staircaseFloor: originalStair.staircaseFloor,
            });

            if (stairWithMargin) {
                band.remainingBand = band.remainingBand.difference(stairWithMargin);
            }

            if (!band.remainingBand.contains(stairWithMargin)) {
                Logger.logAnalytics({
                    message: logName ?? 'POL: LaizeStairs.calculLaizeStairs',
                    value: {
                        error: 'stairWithMargin not contained in remaining band',
                        context: {
                            laizeCalcProps: laizeCalcProps,
                            stairs: rooms.map((r) => r.stairsSteps),
                        },
                        rawContext: JSON.stringify({
                            laizeCalcProps: laizeCalcProps,
                            stairs: rooms.map((r) => r.stairsSteps),
                        }),
                    },
                });
            }
            currentCursorWidth += rect.width;
            wholeIndex++;
        }
        currentCursorHeight += row.height;
    }
    return { stairsResults, totalLength: solution.totalLength };
};
